import { gql } from "@apollo/client";

// 重跑账单（异步）
export const RERUN_BILL = gql`
  mutation RerunBill($rerunRequest: BillRerunRequestInput!) {
    rerunBill(rerunRequest: $rerunRequest) {
      taskId
      success
      message
    }
  }
`;

// 批量重跑账单
export const RERUN_BATCH_BILLS = gql`
  mutation RerunBatchBills($batchRequest: BatchBillRerunRequestInput!) {
    rerunBatchBills(batchRequest: $batchRequest) {
      successCount
      failureCount
      failures {
        clientId
        periodInstanceId
        reason
      }
    }
  }
`;

// 创建调账任务
export const CREATE_ADJUSTMENT_TASK = gql`
  mutation CreateAdjustmentTask($billId: ID!, $adjustmentRequest: AdjustmentRequestInput!) {
    createAdjustmentTask(billId: $billId, adjustmentRequest: $adjustmentRequest) {
      taskId
      success
      errorMessage
    }
  }
`;

// 提交明细调账（DSP 上传调账明细）
export const CREATE_DETAIL_ADJUSTMENT = gql`
  mutation CreateDetailAdjustment($billId: ID!, $request: DetailAdjustmentRequestInput!) {
    createDetailAdjustment(billId: $billId, request: $request) {
      taskId
      success
      errorMessage
    }
  }
`;

// 确认调账
export const CONFIRM_ADJUSTMENT = gql`
  mutation ConfirmAdjustment($input: ConfirmAdjustmentInput!) {
    confirmAdjustment(input: $input) {
      success
      message
    }
  }
`;

// 取消调账
export const CANCEL_ADJUSTMENT = gql`
  mutation CancelAdjustment($input: CancelAdjustmentInput!) {
    cancelAdjustment(input: $input) {
      success
      message
    }
  }
`;

// 导出账单
export const EXPORT_BILL = gql`
  mutation ExportBill($exportRequest: BillExportRequestInput!) {
    exportBill(exportRequest: $exportRequest) {
      success
      errorMessage
      taskIds
    }
  }
`;

export type ExportBillLanguage = 'EN' | 'ZH' | 'ES';

// DSP结算数据汇总一键导出（根据 DSP客户、账单时间范围 查询 ads_settlement_dsp_summary_di 导出 Excel）
export const EXPORT_DSP_SUMMARY_TO_EXCEL = gql`
  mutation ExportDspSummaryToExcel($dspIds: [Long!], $billTimeRange: BillTimeRangeInput) {
    exportDspSummaryToExcel(dspIds: $dspIds, billTimeRange: $billTimeRange)
  }
`;

// DSP结算数据汇总一键导出（异步任务，提交至下载中心）
export const CREATE_DSP_SUMMARY_DOWNLOAD_TASK = gql`
  mutation CreateDspSummaryDownloadTask($dspIds: [Long!], $billTimeRange: BillTimeRangeInput) {
    createDspSummaryDownloadTask(dspIds: $dspIds, billTimeRange: $billTimeRange)
  }
`;

export const CREATE_DSP_SUMMARY_EXPORT_TASK = gql`
  mutation CreateDspSummaryExportTask($dspIds: [Long!], $billTimeRange: BillTimeRangeInput) {
    createDspSummaryExportTask(dspIds: $dspIds, billTimeRange: $billTimeRange)
  }
`;

// DSP账单列表导出（按当前筛选条件全量导出，异步任务，提交至下载中心）
export const EXPORT_DSP_BILL_LIST = gql`
  mutation ExportDspBillList($filters: BillFiltersInput) {
    exportDspBillList(filters: $filters)
  }
`;

// WMS DSP结算数据汇总一键导出（异步任务，提交至下载中心）
export const CREATE_WMS_DSP_SUMMARY_EXPORT_TASK = gql`
  mutation CreateWmsDspSummaryExportTask($warehouseId: Long!, $billTimeRange: BillTimeRangeInput) {
    createWmsDspSummaryExportTask(warehouseId: $warehouseId, billTimeRange: $billTimeRange)
  }
`;

// 确认账单
export const CONFIRM_BILL = gql`
  mutation ConfirmBill($billId: ID!, $remark: String) {
    confirmBill(billId: $billId, remark: $remark) {
      success
      errorMessage
    }
  }
`;

export const CONFIRM_BILL_INTERNAL = gql`
  mutation ConfirmBillInternal($billId: ID!, $remark: String) {
    confirmBillInternal(billId: $billId, remark: $remark) {
      success
      errorMessage
    }
  }
`;

// 批量内部确认账单（DSP 列表；后端仅允许 FINANCIAL_TEAM / DSP_FINANCIAL_TEAM）
export const BATCH_CONFIRM_BILLS = gql`
  mutation BatchConfirmBills($billIds: [ID!]!, $remark: String) {
    batchConfirmBills(billIds: $billIds, remark: $remark) {
      successCount
      failureCount
      failures {
        billId
        reason
      }
    }
  }
`;

// 批量内部确认收入侧账单；与 DSP 批量接口隔离
export const BATCH_CONFIRM_REVENUE_BILLS = gql`
  mutation BatchConfirmRevenueBills($billIds: [ID!]!, $remark: String) {
    batchConfirmRevenueBills(billIds: $billIds, remark: $remark) {
      successCount
      failureCount
      failures {
        billId
        reason
      }
    }
  }
`;

// 外部驳回账单
export const REJECT_BILL_EXTERNAL = gql`
  mutation RejectBillExternal($billId: ID!, $remark: String!) {
    rejectBillExternal(billId: $billId, remark: $remark) {
      success
      errorMessage
    }
  }
`;

// 总部代外部确认账单（收入侧；需先上传凭证拿到 objectKey）
export const CONFIRM_BILL_ON_BEHALF_EXTERNAL = gql`
  mutation ConfirmBillOnBehalfExternal(
    $billId: ID!
    $voucherObjectKey: String!
    $voucherFileName: String
    $remark: String
  ) {
    confirmBillOnBehalfExternal(
      billId: $billId
      voucherObjectKey: $voucherObjectKey
      voucherFileName: $voucherFileName
      remark: $remark
    ) {
      success
      errorMessage
    }
  }
`;

// 总部代外部驳回账单（收入侧）
export const REJECT_BILL_ON_BEHALF_EXTERNAL = gql`
  mutation RejectBillOnBehalfExternal($billId: ID!, $remark: String!) {
    rejectBillOnBehalfExternal(billId: $billId, remark: $remark) {
      success
      errorMessage
    }
  }
`;

// 生成账期规则
export const GENERATE_BILLING_PERIOD_RULE = gql`
  mutation GenerateBillingPeriodRule($clientId: Long!, $billType: BillTypeEnum!) {
    generateBillingPeriodRule(clientId: $clientId, billType: $billType) {
      success
      ruleId
      message
    }
  }
`;

// 删除账期实例
export const DELETE_BILLING_PERIOD_INSTANCE = gql`
  mutation DeleteBillingPeriodInstance($instanceId: Long!) {
    deleteBillingPeriodInstance(instanceId: $instanceId) {
      success
      message
    }
  }
`;

// 重生成账期实例
export const REGENERATE_BILLING_PERIOD_INSTANCE = gql`
  mutation RegenerateBillingPeriodInstance($instanceId: Long!) {
    regenerateBillingPeriodInstance(instanceId: $instanceId) {
      success
      message
    }
  }
`;

// 刷新账期实例（根据账期规则补齐账期）
export const REPLENISH_BILLING_PERIOD_INSTANCES = gql`
  mutation ReplenishBillingPeriodInstances($ruleId: Long!) {
    replenishBillingPeriodInstances(ruleId: $ruleId) {
      success
      replenishedCount
      message
    }
  }
`;

// 创建历史账期实例
export const CREATE_HISTORICAL_PERIOD_INSTANCE = gql`
  mutation CreateHistoricalPeriodInstance($ruleId: ID!, $periodStartTime: String!) {
    createHistoricalPeriodInstance(ruleId: $ruleId, periodStartTime: $periodStartTime) {
      instanceId
      success
      message
    }
  }
`;

// 取消账单汇总任务
export const CANCEL_BILL_SUMMARY = gql`
  mutation CancelBillSummary($taskId: ID!) {
    cancelBillSummary(taskId: $taskId) {
      taskId
      success
      message
    }
  }
`;

// ==================== 测试相关 Mutations ====================

// 创建Mock费用项数据
export const CREATE_MOCK_FEE_ITEMS = gql`
  mutation CreateMockFeeItems($clientId: Long!, $billType: BillTypeEnum!) {
    createMockFeeItems(clientId: $clientId, billType: $billType) {
      success
      message
      dataCount
    }
  }
`;

// 创建Mock账单类型费用项配置
export const CREATE_MOCK_BILL_TYPE_FEE_ITEM_CONFIGS = gql`
  mutation CreateMockBillTypeFeeItemConfigs($clientId: Long!, $billType: BillTypeEnum!) {
    createMockBillTypeFeeItemConfigs(clientId: $clientId, billType: $billType) {
      success
      message
      dataCount
    }
  }
`;

// 创建Mock清分数据
export const CREATE_MOCK_VOUCHER_CLEARINGS = gql`
  mutation CreateMockVoucherClearings(
    $clientId: Long!
    $billType: BillTypeEnum!
    $count: Int!
    $periodStart: String!
    $periodEnd: String!
  ) {
    createMockVoucherClearings(
      clientId: $clientId
      billType: $billType
      count: $count
      periodStart: $periodStart
      periodEnd: $periodEnd
    ) {
      success
      message
      dataCount
    }
  }
`;

// 执行账单汇总测试
export const EXECUTE_BILL_SUMMARY_TEST = gql`
  mutation ExecuteBillSummaryTest(
    $clientId: Long!
    $billType: BillTypeEnum!
    $periodStart: String!
    $periodEnd: String!
  ) {
    executeBillSummaryTest(
      clientId: $clientId
      billType: $billType
      periodStart: $periodStart
      periodEnd: $periodEnd
    ) {
      success
      message
      billId
      summaryCount
      totalAmount
    }
  }
`;

// 清理测试数据
export const CLEANUP_TEST_DATA = gql`
  mutation CleanupTestData($clientId: Long!, $billType: BillTypeEnum!) {
    cleanupTestData(clientId: $clientId, billType: $billType) {
      success
      message
      dataCount
    }
  }
`;

// 新增账期规则版本
export const CREATE_BILLING_PERIOD_RULE_VERSION = gql`
  mutation CreateBillingPeriodRuleVersion($input: CreateBillingPeriodRuleVersionInput!) {
    createBillingPeriodRuleVersion(input: $input) {
      success
      ruleId
      message
    }
  }
`;
